/**
 * 依赖注入容器 —— **唯一持有实现类**的地方（ARCH §1.3）。
 *
 * 规则：
 *  - UI 层（`components/`、`features/`）只能 import `services/*` 与 `store/*`，
 *    出现任何 `Mock*` / `Relay*` / `crypto/*` 直接引用即 Review 打回；
 *  - 五个可替换点（`IChainClient` / `IContentStore` / `ISessionCipher` /
 *    `ITransport` / `IStorage`）的接线**全部**在这里，切换实现不改任何调用点。
 *
 * T03（网络）与 T04（内容/链）只需调用 `setXxx()` 完成装配，不需要改动本文件以外的代码。
 */

import type { IChainClient } from '@shared/chain';
import type { IStorage } from '@/storage/IStorage';
import { createStorage } from '@/storage';
import { createChainRpcClient, MockChainRpcClient } from '@/chain/MockChainRpcClient';
import { MockChainLocalClient } from '@/chain/MockChainLocalClient';
import type { Libp2pBrowserPeer } from '@/net/peer';
import { IdentityService } from './IdentityService';
import { ChatService, type SessionCipherFactory } from './ChatService';
import { FeedService } from './FeedService';
import { WalletService } from './WalletService';
import { SocialService } from './SocialService';
import { EcdhAesGcmCipher } from '@/crypto/session';
import type { ITransport } from '@/net/ITransport';
import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';
import { CHAIN_MODE, CHAIN_WS_URL } from '@/config';

const logger = createLogger('container');

export interface Container {
  /** 存储（默认 IndexedDbStorage）。 */
  readonly storage: IStorage;
  /** 身份服务。 */
  readonly identity: IdentityService;
  /** 链客户端（shared WS 账本 或 浏览器内本地账本）。 */
  readonly chain: IChainClient;
  /** 聊天服务（T03；首次访问时按需装配，需要已解锁）。 */
  readonly chat: ChatService;
  /** 广场服务（T04；发布 / 历史 / 实时订阅 / 图片解析）。 */
  readonly feed: FeedService;
  /** 钱包服务（T04；余额 / 转账 / 水龙头 / 交易历史）。 */
  readonly wallet: WalletService;
  /** 社交图谱服务（T04；关注 / 取关 / 关注列表）。 */
  readonly social: SocialService;
  hasContentStore(): boolean;
  hasTransport(): boolean;
  hasSessionCipherFactory(): boolean;
  hasPeerClient(): boolean;
  getPeerClient(): Libp2pBrowserPeer;
  setPeerClient(peer: Libp2pBrowserPeer): void;
  /** 锁定时重置会话级单例（保留 storage / identity / chain）。 */
  reinitSession(): void;
}

/** 内容存储（T04 装配）。 */
export interface MinimalContentStore {
  put(bytes: Uint8Array, mime: string): Promise<unknown>;
  get(cid: string): Promise<Uint8Array | null>;
  has(cid: string): Promise<boolean>;
}
/** 传输层（T03 装配）。 */
export interface MinimalTransport {
  start(addr: string): Promise<void>;
  stop(): Promise<void>;
}

class DefaultContainer implements Container {
  private _storage: IStorage | null = null;
  private _identity: IdentityService | null = null;
  private _chain: IChainClient | null = null;

  private _contentStore: MinimalContentStore | null = null;
  private _transport: MinimalTransport | null = null;
  private _sessionCipherFactory: SessionCipherFactory | null = null;
  private _chat: ChatService | null = null;
  private _feed: FeedService | null = null;
  private _wallet: WalletService | null = null;
  private _social: SocialService | null = null;
  private _peerClient: Libp2pBrowserPeer | null = null;

  // ─────────────── 基础装配 ───────────────

  get storage(): IStorage {
    if (!this._storage) this._storage = createStorage();
    return this._storage;
  }

  get chain(): IChainClient {
    if (!this._chain) {
      this._chain =
        CHAIN_MODE === 'local' ? new MockChainLocalClient() : createChainRpcClient(CHAIN_WS_URL);
      logger.info('链客户端已装配', { mode: CHAIN_MODE });
    }
    return this._chain;
  }

  get identity(): IdentityService {
    if (!this._identity) {
      this._identity = new IdentityService(this.storage, this.chain);
    }
    return this._identity;
  }

  get chat(): ChatService {
    if (!this._chat) {
      this._chat = new ChatService({
        storage: this.storage,
        identity: this.identity,
        transport: this.getTransport() as unknown as ITransport,
        cipherFactory: this.getSessionCipherFactory(),
      });
    }
    return this._chat;
  }

  get feed(): FeedService {
    if (!this._feed) {
      const peer = this.getPeerClient();
      this._feed = new FeedService({
        peer,
        content: this.getContentStore(),
        storage: this.storage,
        identity: this.identity,
        chain: this.chain,
      });
    }
    return this._feed;
  }

  get wallet(): WalletService {
    if (!this._wallet) {
      this._wallet = new WalletService({ chain: this.chain, identity: this.identity, storage: this.storage });
    }
    return this._wallet;
  }

  get social(): SocialService {
    if (!this._social) {
      this._social = new SocialService({ chain: this.chain, identity: this.identity, storage: this.storage });
    }
    return this._social;
  }

  // ─────────────── 扩展点（T03 / T04 注入） ───────────────

  /** T04：注入 `IContentStore` 实现（默认 `RelayContentStore`）。 */
  setContentStore(store: MinimalContentStore): void {
    this._contentStore = store;
  }

  getContentStore(): MinimalContentStore {
    if (!this._contentStore) throw new MeshLinkError('E_NOT_IMPLEMENTED', '内容存储尚未装配（T04）');
    return this._contentStore;
  }

  hasContentStore(): boolean {
    return this._contentStore !== null;
  }

  /** T03：注入 `ITransport` 实现（默认 `HybridTransport`）。 */
  setTransport(transport: MinimalTransport): void {
    this._transport = transport;
  }

  getTransport(): MinimalTransport {
    if (!this._transport) throw new MeshLinkError('E_NOT_IMPLEMENTED', '传输层尚未装配（T03）');
    return this._transport;
  }

  hasTransport(): boolean {
    return this._transport !== null;
  }

  /** T03：注入 `ISessionCipher` 工厂（默认 `EcdhAesGcmCipher`）。 */
  setSessionCipherFactory(factory: SessionCipherFactory): void {
    this._sessionCipherFactory = factory;
  }

  getSessionCipherFactory(): SessionCipherFactory {
    if (!this._sessionCipherFactory) {
      // 缺省回落到 EcdhAesGcmCipher，保证未显式装配时也能用
      return (opts) => new EcdhAesGcmCipher(opts);
    }
    return this._sessionCipherFactory;
  }

  hasSessionCipherFactory(): boolean {
    return this._sessionCipherFactory !== null;
  }

  /** 替换链实现（T04 的本地降级 / 真实链）。 */
  setChainClient(client: IChainClient): void {
    this._chain = client;
    // 身份服务持有的链引用一并刷新
    if (this._identity) this._identity.setChainClient(client);
  }

  /** 替换存储实现（测试用）。 */
  setStorage(storage: IStorage): void {
    this._storage = storage;
    this._identity = null;
  }

  // ─────────────── 节点 / 会话装配（T04） ───────────────

  getPeerClient(): Libp2pBrowserPeer {
    if (!this._peerClient) throw new MeshLinkError('E_NOT_IMPLEMENTED', '传输节点尚未装配（T03）');
    return this._peerClient;
  }

  setPeerClient(peer: Libp2pBrowserPeer): void {
    this._peerClient = peer;
  }

  hasPeerClient(): boolean {
    return this._peerClient !== null;
  }

  /** 锁定时重置会话级单例（传输 / 内容 / Feed / Chat / 钱包 / 社交），保留 storage / identity / chain。 */
  reinitSession(): void {
    this._feed = null;
    this._wallet = null;
    this._social = null;
    this._peerClient = null;
    this._transport = null;
    this._contentStore = null;
    // 注意：保留 _storage / _identity / _chain
  }

  /** 注销：清空全部单例并关闭连接。 */
  async reset(): Promise<void> {
    this._identity?.lock();
    if (this._chain && typeof (this._chain as MockChainRpcClient).close === 'function') {
      await (this._chain as MockChainRpcClient).close().catch(() => undefined);
    }
    await this._storage?.close().catch(() => undefined);
    this._identity = null;
    this._chain = null;
    this._storage = null;
    this._contentStore = null;
    this._transport = null;
    this._sessionCipherFactory = null;
    this._feed = null;
    this._wallet = null;
    this._social = null;
    this._peerClient = null;
  }
}

export const container: Container & {
  setContentStore(s: MinimalContentStore): void;
  getContentStore(): MinimalContentStore;
  setTransport(t: MinimalTransport): void;
  getTransport(): MinimalTransport;
  setSessionCipherFactory(f: SessionCipherFactory): void;
  getSessionCipherFactory(): SessionCipherFactory;
  setChainClient(c: IChainClient): void;
  setStorage(s: IStorage): void;
  setPeerClient(p: Libp2pBrowserPeer): void;
  getPeerClient(): Libp2pBrowserPeer;
  hasPeerClient(): boolean;
  reinitSession(): void;
  reset(): Promise<void>;
} = new DefaultContainer();

export default container;
